// src/pages/JobFestOnePager.tsx
import React, { useEffect, useState } from "react";
import "./JobFestOnePager.css";

import jobfestAvatar from "../assets/jobfest/jobfest-avatar.png";

const COMPANY_LINK =
  "https://calendly.com/sales-babblebots/babblebots-discovery";
const CANDIDATE_LINK = "https://forms.gle/QCVcuYtnx6K8gp616";

// Dec 13, 2025 – Job Fest start
const FEST_START = new Date("2025-12-13T00:00:00+05:30");

// text for typewriter line
const TYPEWRITER_TEXT = "AI HIRING";

const openInNewTab = (url: string) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

const JobFestOnePager: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
  });

  const [typedText, setTypedText] = useState("");

  // lightweight countdown (desktop + mobile)
  useEffect(() => {
    const tick = () => {
      const now = new Date().getTime();
      const diff = FEST_START.getTime() - now;

      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0 });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setTimeLeft({ days, hours, minutes });
    };

    tick(); // initial
    const id = window.setInterval(tick, 60_000); // update every min

    return () => window.clearInterval(id);
  }, []);

  // typewriter effect for "AI HIRING"
  useEffect(() => {
    let index = 0;
    const intervalId = window.setInterval(() => {
      index += 1;
      if (index <= TYPEWRITER_TEXT.length) {
        setTypedText(TYPEWRITER_TEXT.slice(0, index));
      } else {
        window.clearInterval(intervalId);
      }
    }, 110); // typing speed

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <main className="jf-brochure-page">
      {/* HERO */}
      <section className="jf-hero-shell">
        <header className="jf-hero-header">
          <div className="jf-hero-logo-block">
            <p className="jf-hero-logo-title">BABBLEBOTS JOB FEST</p>

            {/* typewriter line */}
            <p className="jf-hero-logo-sub" aria-label={TYPEWRITER_TEXT}>
              <span className="jf-typewriter">
                {typedText}
                <span className="jf-typewriter-cursor" />
              </span>
            </p>
          </div>

          <div className="jf-hero-date-pill">13–19 Dec, 2025</div>
        </header>

        <div className="jf-hero-body">
          <div className="jf-hero-copy">
            {/* Both things important: event + top 5% talent */}
            <div className="jf-hero-eyebrow-row">
              <span className="jf-hero-tag">December AI Hiring Sprint</span>
              <span className="jf-hero-tag jf-hero-tag--purple">
                Top 5% AI talent in India
              </span>
            </div>

            <h1 className="jf-hero-title">
              Close your
              <br />
              AI position
              <br />
              in <span className="jf-hero-title-highlight">7 days</span>
            </h1>

            <p className="jf-hero-subdate">Dec 13–19, 2025 · Pan-India cohort</p>

            {/* small, subtle countdown on left side */}
            <div className="jf-hero-countdown">
              <p className="jf-hero-count-label">Job Fest starts in</p>
              <div className="jf-hero-count-values">
                <span className="jf-hero-count-number">{timeLeft.days}</span>
                <span className="jf-hero-count-unit">d</span>
                <span className="jf-hero-count-sep">•</span>
                <span className="jf-hero-count-number">{timeLeft.hours}</span>
                <span className="jf-hero-count-unit">h</span>
                <span className="jf-hero-count-sep">•</span>
                <span className="jf-hero-count-number">{timeLeft.minutes}</span>
                <span className="jf-hero-count-unit">m</span>
              </div>
            </div>

            <p className="jf-hero-subtext">
              Join our December AI Talent Fest and hire AI Engineers &amp; AI
              Interns from a curated, pre-screened pan-India cohort.
            </p>
          </div>

          <div className="jf-hero-avatar-wrap">
            <div className="jf-hero-avatar-ring">
              <img
                src={jobfestAvatar}
                alt="Babblebots AI Talent Fest"
                className="jf-hero-avatar-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* TWO MAIN CARDS */}
      <section className="jf-cards-shell">
        <div className="jf-cards-row">
          {/* COMPANY CARD */}
          <div
            className="jf-card jf-card--company"
            onClick={() => openInNewTab(COMPANY_LINK)}
          >
            <div className="jf-card-inner">
              <span className="jf-card-chip jf-card-chip--company">
                For Companies
              </span>

              <div className="jf-card-section">
                <p className="jf-card-section-label">Talent Available</p>
                <ul className="jf-card-bullet-list">
                  <li>
                    <span className="jf-dot" />
                    AI Engineer
                  </li>
                  <li>
                    <span className="jf-dot" />
                    AI Intern
                  </li>
                </ul>
              </div>

              <div className="jf-card-section">
                <p className="jf-card-section-label">Timeline</p>

                <div className="jf-card-timeline">
                  <div className="jf-card-timeline-row">
                    <span className="jf-card-timeline-label">Registration</span>
                    <span className="jf-card-timeline-value">
                      8–12 Dec 2025
                    </span>
                  </div>
                  <div className="jf-card-timeline-row">
                    <span className="jf-card-timeline-label">
                      AI Interview Fest
                    </span>
                    <span className="jf-card-timeline-value">
                      13–16 Dec 2025
                    </span>
                  </div>
                  <div className="jf-card-timeline-row">
                    <span className="jf-card-timeline-label">
                      Human interviews and offers
                    </span>
                    <span className="jf-card-timeline-value">
                      17–19 Dec 2025
                    </span>
                  </div>
                  <div className="jf-card-timeline-row">
                    <span className="jf-card-timeline-label">Seats</span>
                    <span className="jf-card-timeline-value jf-text-highlight">
                      10 max companies
                    </span>
                  </div>
                </div>
              </div>

              <div className="jf-card-cta-wrap">
                <button
                  type="button"
                  className="jf-pill-cta jf-pill-cta--dark"
                  onClick={(e) => {
                    e.stopPropagation();
                    openInNewTab(COMPANY_LINK);
                  }}
                >
                  <span>Book demo &amp; reserve seat</span>
                  <span className="jf-pill-cta-arrow">↗</span>
                </button>
                <p className="jf-card-cta-help">
                  Lock your company’s slot &amp; share AI hiring needs.
                </p>
              </div>
            </div>
          </div>

          {/* CANDIDATE CARD */}
          <div
            className="jf-card jf-card--candidate"
            onClick={() => openInNewTab(CANDIDATE_LINK)}
          >
            <div className="jf-card-inner">
              <span className="jf-card-chip jf-card-chip--candidate">
                For Candidates
              </span>

              <div className="jf-card-section">
                <p className="jf-card-section-label">Open: 30 AI roles</p>
                <ul className="jf-card-bullet-list">
                  <li>
                    <span className="jf-dot jf-dot--purple" />
                    AI Engineer
                  </li>
                  <li>
                    <span className="jf-dot jf-dot--purple" />
                    AI Intern
                  </li>
                </ul>
              </div>

              <div className="jf-card-section">
                <p className="jf-card-section-label">Timeline</p>

                <div className="jf-card-timeline">
                  <div className="jf-card-timeline-row">
                    <span className="jf-card-timeline-label">
                      AI-interviews and shortlisting
                    </span>
                    <span className="jf-card-timeline-value">
                      13–16 Dec 2025
                    </span>
                  </div>
                  <div className="jf-card-timeline-row">
                    <span className="jf-card-timeline-label">Job type</span>
                    <span className="jf-card-timeline-value">
                      Remote · Hybrid · On-site
                    </span>
                  </div>
                  <div className="jf-card-timeline-row">
                    <span className="jf-card-timeline-label">Experience</span>
                    <span className="jf-card-timeline-value">
                      AI Engineer: 1–4 yrs · AI Intern: 0–1 yrs
                    </span>
                  </div>
                  <div className="jf-card-timeline-row">
                    <span className="jf-card-timeline-label">
                      Applications
                    </span>
                    <span className="jf-card-timeline-value jf-text-highlight">
                      Up to 1000 candidates
                    </span>
                  </div>
                </div>
              </div>

              <div className="jf-card-cta-wrap">
                <button
                  type="button"
                  className="jf-pill-cta jf-pill-cta--dark"
                  onClick={(e) => {
                    e.stopPropagation();
                    openInNewTab(CANDIDATE_LINK);
                  }}
                >
                  <span>Apply now – take AI interview</span>
                  <span className="jf-pill-cta-arrow">→</span>
                </button>
                <p className="jf-card-cta-help">
                  Upload your CV &amp; complete round one on{" "}
                  <span className="jf-link-inline">babblebots.ai</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

     
      {/* FINAL CTA STRIP */}
      <section className="jf-bottom-cta">
        <h2 className="jf-bottom-cta-title">
          Ready to transform your hiring?
        </h2>
        <p className="jf-bottom-cta-sub">
          Join the December cohort and access India’s top 5% AI talent.
        </p>

        <button
          type="button"
          className="jf-bottom-cta-btn"
          onClick={() => openInNewTab(COMPANY_LINK)}
        >
          Connect with us
        </button>
      </section>

     {/* TRUST SECTION */}
<section className="jf-trust-shell">
  <div className="jf-trust-grid">

    {/* CARD 1 — TRUSTED RESULTS */}
    <div className="jf-trust-card">
      <p className="jf-trust-title">TRUSTED RESULTS</p>

      <p className="jf-trust-text">
        Backed by <strong>Indus Towers, Ajmera, TIH, Bharat Gen & Growisto</strong> —
        250+ companies & 2000+ roles filled.
      </p>
    </div>

    {/* CARD 2 — BABBLEBOTS LOVED */}
    <div className="jf-trust-card">
      <p className="jf-trust-title"></p>

      <p className="jf-trust-text">
        Babblebots AI is <span className="jf-heart">♥️</span> by candidates —
        <strong>2,00,000+ vetted</strong> across India.
      </p>
    </div>

  </div>
</section>



    </main>
  );
};

export default JobFestOnePager;
